import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FAQ, { getFAQSchema } from '@/pages/home/components/FAQ';
import { useEffect } from 'react';

const siteUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SITE_URL) || 'https://biffdigital.com';
const pagePath = '/hizmetler/web-gelistirme';
const pageUrl = `${siteUrl}${pagePath}`;

const webFAQItems = [
  {
    question: 'Web sitesi yaptırırken nelere dikkat etmeliyim?',
    answer:
      'Modern bir web sitesi hız (Core Web Vitals), mobil uyumluluk, SEO altyapısı, güvenlik ve dönüşüm odaklı UX tasarımını bir arada sunmalıdır. Tasarım başlangıçtır; asıl olan sitenin bir satış ve dönüşüm sistemi olarak çalışmasıdır.',
  },
  {
    question: 'Kurumsal web sitesi ile e-ticaret sitesi arasındaki fark nedir?',
    answer:
      'Kurumsal web sitesi markanızın dijital vitrini ve güven inşa aracıdır. E-ticaret sitesi ise bir satış makinesidir. Farklı kullanıcı davranışları, farklı dönüşüm hedefleri ve farklı teknik altyapılar gerektirir.',
  },
  {
    question: 'Web sitesi projesi ne kadar sürer?',
    answer:
      'Kurumsal bir web sitesi projesi 4-8 hafta, e-ticaret projesi 8-16 hafta arasında tamamlanır. Özel yazılım projeleri kapsama göre değişir. Proje başlangıcında net bir timeline ve milestone planıyla çalışırız.',
  },
  {
    question: 'AI entegrasyonu web siteme ne katar?',
    answer:
      'AI entegrasyonu; kişiselleştirilmiş kullanıcı deneyimi, akıllı ürün önerileri, chatbot destek, otomatik içerik optimizasyonu ve kullanıcı davranışına dayalı dinamik sayfa düzeni gibi yetenekler kazandırır.',
  },
  {
    question: 'Mevcut web sitemi yenilemek mi yoksa sıfırdan yapmak mı daha iyi?',
    answer:
      'Mevcut sitenin teknik altyapısı, performansı, mobil uyumluluğu ve dönüşüm oranlarına bağlıdır. Eğer altyapı güncel ve ölçeklenebilirse yenileme yeterli olabilir. Ancak çoğu durumda, eski altyapılar modern beklentileri karşılamadığı için sıfırdan geliştirme daha verimli olur.',
  },
  {
    question: 'Web sitesi hızı neden bu kadar önemli?',
    answer:
      'Sayfa hızı hem kullanıcı deneyimi hem SEO için kritiktir. 3 saniyeden uzun yüklenen sitelerde kullanıcıların %53\'ü siteyi terk eder. Google, Core Web Vitals metriklerini sıralama faktörü olarak kullanır. Hızlı site = daha yüksek dönüşüm + daha iyi sıralama.',
  },
];

const faqSchema = getFAQSchema(webFAQItems);

const pageSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      name: 'Web Geliştirme & Yazılım Hizmeti',
      provider: {
        '@type': 'Organization',
        name: 'BIFF Digital',
        url: siteUrl,
        telephone: '+905557101968',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Söğütözü Cad. No: 2 Koç Kuleleri C Blok 17',
          addressLocality: 'Çankaya',
          addressRegion: 'Ankara',
          addressCountry: 'TR',
          postalCode: '06530',
        },
      },
      description:
        'Kurumsal web sitesi, e-ticaret, özel yazılım ve AI entegrasyonuyla web sitenizi bir satış sistemine dönüştürüyoruz. BIFF Digital web geliştirmeyi strateji ve dönüşümle birlikte yönetir.',
      url: pageUrl,
      areaServed: { '@type': 'Country', name: 'Turkey' },
      serviceType: 'Web Geliştirme & Yazılım',
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Web Geliştirme Hizmet Detayları',
        itemListElement: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Kurumsal Web Siteleri', description: 'Markanızı en iyi yansıtan, dönüşüm odaklı kurumsal web siteleri' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'E-Ticaret', description: 'Ölçeklenebilir, hızlı ve dönüşüm odaklı e-ticaret platformları' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Özel Yazılım & AI Entegrasyonu', description: 'İş süreçlerinize özel yazılım çözümleri ve yapay zeka entegrasyonları' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'UX/UI Tasarım', description: 'Kullanıcı davranışına dayalı arayüz ve deneyim tasarımı' } },
        ],
      },
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${siteUrl}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: 'Web Geliştirme & Yazılım', item: pageUrl },
      ],
    },
    {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.speakable-summary'],
    },
    faqSchema,
  ],
};

export default function WebGelistirme() {
  useEffect(() => {
    document.title = 'Web Geliştirme & Yazılım | Kurumsal Site & E-Ticaret | BIFF Digital';

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', pageUrl);

    return () => {
      link?.setAttribute('href', siteUrl);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageSchema) }}
      />
      <Navbar />
      <main className="pt-16 md:pt-20">

        {/* HERO */}
        <section className="section-padding pb-0 md:pb-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-4">HİZMETLER</p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground-50 tracking-tighter leading-none mb-8">
              Web<br />Geliştirme
            </h1>
            <div className="h-px w-full bg-gradient-to-r from-white/15 via-white/5 to-transparent mb-10" />
            <p className="text-xl md:text-2xl text-foreground-200 font-medium leading-relaxed mb-6">
              Web Siteniz Bir Vitrin Değil.<br />
              <span className="text-primary-500">Satış Sisteminizdir.</span>
            </p>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed max-w-2xl speakable-summary">
              Tasarım başlangıçtır. Sistem asıl işidir. Güzel bir web sitesi yaptırmak kolay. Kullanıcıyı anlayan, dönüşüme götüren ve markanın büyüme motoru olarak çalışan bir sistem kurmak ise bizim işimiz.
            </p>
          </div>
        </section>

        {/* Tasarım başlangıçtır */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-8">
              Tasarım Başlangıçtır.<br />Sistem Asıl İşidir.
            </h2>
            <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6">
              Bir web sitesi yayına girdiğinde iş bitmez — başlar. Kullanıcılar sitenize gelir, gezinir, karar verir, aksiyon alır ya da çıkıp gider. Biz sitenizi bu yolculuğun her adımında çalışan bir sistem olarak tasarlıyoruz.
            </p>
            <div className="glass-card rounded-2xl p-6 md:p-8 border-l-4 border-primary-500 mb-8">
              <p className="text-base md:text-lg text-foreground-50 font-bold leading-relaxed">
                Kullanıcı sitenize geldiğinde ne oluyor?
              </p>
              <p className="text-sm text-foreground-300 mt-3 leading-relaxed">
                Bu sorunun cevabını veriyle biliyorsanız, web siteniz bir sistemdir. Bilmiyorsanız, sadece bir sayfadır.
              </p>
            </div>
          </div>
        </section>

        {/* Ne Yapıyoruz */}
        <section className="section-padding bg-background-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10 md:mb-14">
              <p className="text-primary-500 font-bold text-sm tracking-widest uppercase mb-2">NE YAPIYORUZ?</p>
              <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter">
                Koddan Deneyime<br />Tam Kapsamlı Geliştirme
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-5 md:gap-6">
              {[
                { icon: 'ri-building-4-line', title: 'Kurumsal Web Siteleri', desc: 'Markanızı en iyi yansıtan, hızlı, mobil uyumlu ve dönüşüm odaklı kurumsal siteler. SEO altyapısıyla birlikte geliştiriyor, yayına aldığımız günden itibaren organik görünürlüğe hazır hale getiriyoruz.' },
                { icon: 'ri-shopping-cart-2-line', title: 'E-Ticaret', desc: 'Ölçeklenebilir e-ticaret platformları. Ürün yönetimi, ödeme entegrasyonu, stok takibi ve dönüşüm hunisiyle birlikte kuruyoruz. Amaç sadece satış değil, tekrar eden müşteri davranışı inşa etmek.' },
                { icon: 'ri-smartphone-line', title: 'UX/UI Tasarım', desc: 'Kullanıcı davranışı araştırmasıyla başlıyor, wireframe ve prototiplerle ilerliyoruz. Her ekranın, her butonun, her geçişin bir amacı var. Güzel değil, işlevsel tasarım yapıyoruz.' },
                { icon: 'ri-robot-2-line', title: 'AI Entegrasyonu', desc: 'Yapay zeka ile kişiselleştirilmiş kullanıcı deneyimi, akıllı öneri sistemleri, chatbotlar ve otomasyon. AI\'ı sitenize entegre ediyor, kullanıcıya özel dinamik deneyimler sunuyoruz.' },
                { icon: 'ri-settings-3-line', title: 'Özel Yazılım', desc: 'Hazır çözümlerin yetmediği yerde, iş süreçlerinize özel yazılım çözümleri geliştiriyoruz. CRM entegrasyonları, dashboard\'lar, veri otomasyonları — ihtiyacınız neyse onu inşa ediyoruz.' },
                { icon: 'ri-speed-up-line', title: 'Performans & Analytics', desc: 'Core Web Vitals optimizasyonu, sayfa hızı, mobil performans ve kullanıcı davranışı analitiği. Siteniz hızlı açılmıyorsa, kullanıcıyı daha ilk saniyede kaybedersiniz.' },
              ].map((item, i) => (
                <div key={i} className="glass-card-hover rounded-2xl p-6 md:p-8 group">
                  <div className="w-12 h-12 bg-background-200 rounded-xl flex items-center justify-center mb-5 group-hover:bg-primary-500/10 transition-colors duration-300">
                    <i className={`${item.icon} text-xl text-primary-500`} />
                  </div>
                  <h3 className="text-lg font-bold text-foreground-50 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Süreç */}
        <section className="section-padding">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-3xl md:text-4xl text-foreground-50 tracking-tighter mb-10">
              Geliştirme Sürecimiz
            </h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Keşif & Planlama', desc: 'İhtiyaç analizi, kullanıcı araştırması, teknik gereksinimlerin belirlenmesi ve proje planının oluşturulması.' },
                { step: '02', title: 'UX/UI Tasarım', desc: 'Wireframe, prototip ve görsel tasarım. Her ekranın kullanıcıyı bir sonraki adıma götürmesini sağlıyoruz.' },
                { step: '03', title: 'Geliştirme', desc: 'Modern teknolojilerle, performans odaklı ve ölçeklenebilir şekilde geliştiriyoruz. Kod kalitesi ve güvenlik standartlarından ödün vermiyoruz.' },
                { step: '04', title: 'Test & Optimizasyon', desc: 'Hız testleri, mobil uyumluluk, SEO kontrolleri ve kullanıcı testleri. Yayına çıkmadan her şeyin çalıştığından emin oluyoruz.' },
                { step: '05', title: 'Lansman & Bakım', desc: 'Yayına alma, izleme ve sürekli iyileştirme. Site canlıya çıktıktan sonra da yanınızdayız.' },
              ].map((item, i) => (
                <div key={i} className="glass-card rounded-2xl p-5 md:p-6 flex items-start gap-5">
                  <span className="text-2xl font-black text-primary-500 font-heading shrink-0">{item.step}</span>
                  <div>
                    <h3 className="text-base font-bold text-foreground-100 mb-1">{item.title}</h3>
                    <p className="text-sm text-foreground-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ
          items={webFAQItems}
          sectionTitle="SIK SORULANLAR"
          sectionSubtitle="WEB GELİŞTİRME"
          sectionSummary="Web geliştirme ve yazılım hakkında en sık sorulan soruları, doğrudan ve net cevaplarla burada topladık."
          speakableClass={false}
        />

        {/* CTA */}
        <section className="section-padding bg-background-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="glass-card rounded-2xl p-8 md:p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-primary-500/5 rounded-bl-full -mr-16 -mt-16" />
              <div className="relative z-10">
                <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl text-foreground-50 tracking-tighter mb-4">
                  Yeni bir web sitesi değil,<br />
                  <span className="text-primary-500">çalışan bir sistem</span> kuralım.
                </h2>
                <p className="text-sm md:text-base text-foreground-400 mb-8 max-w-xl mx-auto leading-relaxed">
                  Mevcut sitenizin ücretsiz performans ve dönüşüm analizini yapalım. Nerede kullanıcı kaybediyorsunuz, birlikte görelim.
                </p>
                <a href="/iletisim" className="btn-primary text-base px-8 py-4">
                  Ücretsiz Site Analizi
                  <i className="ri-arrow-right-line" />
                </a>
              </div>
            </div>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}